/*
  Funções utilitárias reutilizáveis em diferentes partes do projeto.
*/

import i18n from '@/i18n/i18n'

// Mapeamento dos locais suportados
const localeMap = {
  pt: 'pt-BR',
  en: 'en-US',
  es: 'es-ES'
}

// Verifica se houve uma rolagem de tela maior que 300px
export function checkScroll() {
  return window.scrollY > 300;
}

// Resolve o locale atual (usa $i18n se passado, senão navegador)
function getLocale () {
  const current = i18n.global.locale.value || i18n.global.locale
  return localeMap[current] || current || navigator.language
}

// Converte string "2025-10-29 18:20:42" para objeto Date (UTC)
export function parseUTCString (dataOriginal) {
  if (!dataOriginal) return null
  // Garante formato ISO e marca como UTC
  const utcString = dataOriginal.replace(' ', 'T') + 'Z'
  return new Date(utcString)
}

// Formata uma data da API para "dd/mm/aaaa" ou conforme locale do i18n
export function formatarData (dataOriginal) {
  const date = parseUTCString(dataOriginal)
  if (!date) return ''
  return new Intl.DateTimeFormat(getLocale(), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
  }).format(date)
}

// Formata data e hora conforme locale do i18n
export function formatarDataHora (dataOriginal) {
  const date = parseUTCString(dataOriginal);
  if (!date) return '';
  
  const formatter = new Intl.DateTimeFormat(getLocale(), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
  });

  // Usa formatToParts e remove a vírgula
  return formatter.formatToParts(date)
    .map(part => (part.type === 'literal' && part.value.includes(',')) ? ' ' : part.value)
    .join('')
}

// Retorna a data atual no formato do locale do i18n
export function dataAtual () {
  const date = new Date()
  return new Intl.DateTimeFormat(getLocale(), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
  }).format(date)
}

// Converte array ou objeto em uma string de linha única
export function toLine(arrayObject) {
  if (arrayObject == null) return arrayObject;
  if (typeof arrayObject === 'string') return arrayObject;
  if (Array.isArray(arrayObject)) return arrayObject.join(", ");
  return String(arrayObject);
}

// Gera string UTC no MESMO formato da API: "YYYY-MM-DD HH:mm:ss"
export function nowUTCString () {
  const d = new Date()
  const pad = n => String(n).padStart(2, '0')

  const year   = d.getUTCFullYear()
  const month  = pad(d.getUTCMonth() + 1)
  const day    = pad(d.getUTCDate())
  const hour   = pad(d.getUTCHours())
  const minute = pad(d.getUTCMinutes())
  const second = pad(d.getUTCSeconds())

  return `${year}-${month}-${day} ${hour}:${minute}:${second}`
}

/*** Funções para gerenciar imagens no editor HTML ***/

/**
 * Troca src="blob:..." por src="idb://<key>" usando um mapa blobUrl -> key.
 * Útil para salvar HTML "leve" no localStorage.
 */
export function replaceBlobToIdb(html, imageMap = {}) {
  if (!html) return html;

  // troca src
  let out = html.replace(/src="(blob:[^"]+)"/g, (match, blobUrl) => {
    const key = imageMap[blobUrl];
    return key ? `src="idb://${key}"` : match;
  });

  // troca data-mce-src (quando existir)
  out = out.replace(/data-mce-src="(blob:[^"]+)"/g, (match, blobUrl) => {
    const key = imageMap[blobUrl];
    return key ? `data-mce-src="idb://${key}"` : match;
  });

  return out;
}

/**
 * Extrai todas as keys idb://<key> presentes no HTML.
 * Útil para manter this.images atualizado (limpeza, reset, etc).
 */
export function extractIdbKeys(html) {
  if (!html) return [];
  const keys = [];

  const re1 = /src="idb:\/\/([^"]+)"/g;
  const re2 = /data-mce-src="idb:\/\/([^"]+)"/g;

  let m;
  while ((m = re1.exec(html)) !== null) keys.push(m[1]);
  while ((m = re2.exec(html)) !== null) keys.push(m[1]);

  return [...new Set(keys)];
}

/**
 * Converte src="idb://<key>" em src="blob:..." para renderizar no browser.
 * Recria imageMap (blobUrl -> key) e atualiza draft.images.
 */
export async function resolveIdbImagesToBlobUrls(html, draft, scope = 'generic') {
  if (!html || !html.includes('idb://')) return html;

  const { getImage } = await import('@/utils/idb/imagesDb')

  const keys = extractIdbKeys(html);
  let resolved = html;

  const bucketName =
    scope === 'editor' ? 'editorBlobUrls'
    : scope === 'preview' ? 'previewBlobUrls'
    : 'blobUrls';

  draft[bucketName] ||= [];
  draft.imageMap ||= {};
  draft.images ||= [];

  // opcional, mas ajuda a não acumular blobs em reativações repetidas
  // (principalmente no editor)
  // if (scope === 'editor') {
  //   (draft.editorBlobUrls || []).forEach(u => URL.revokeObjectURL(u));
  //   draft.editorBlobUrls = [];
  // }

  for (const key of keys) {
    const blob = await getImage(key);
    if (!blob) continue;

    const blobUrl = URL.createObjectURL(blob);
    draft[bucketName].push(blobUrl);
    draft.imageMap[blobUrl] = key;
    if (!draft.images.includes(key)) draft.images.push(key);

    // troca src
    resolved = resolved
      .replaceAll(`src="idb://${key}"`, `src="${blobUrl}"`)
      // troca data-mce-src (crítico pro TinyMCE)
      .replaceAll(`data-mce-src="idb://${key}"`, `data-mce-src="${blobUrl}"`);
  }

  return resolved;
}

/**
 * Converte data URI (base64) em Blob.
 */
function dataUriToBlob(dataUri) {
  const [meta, b64] = dataUri.split(',');
  const mime = meta.match(/data:(.*?);base64/i)?.[1] || 'application/octet-stream';

  const bin = atob(b64);
  const len = bin.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);

  return new Blob([bytes], { type: mime });
}

/**
 * Gera uma key única para imagens no IndexedDB
 */
function makeIdbImageKey(userId) {
  return `u${userId}-${crypto.randomUUID()}`;
}

/**
 * Migra <img src="data:image/...base64,..."> para IndexedDB e substitui por idb://<key>.
 * Também mantém data-mce-src consistente.
 *
 * IMPORTANTE: isso deve ser usado quando você carrega conteúdo vindo do backend (REA publicado),
 * para que os novos rascunhos já usem IndexedDB.
 */
export async function migrateDataImagesToIdb(html, draft, userId) {
  if (!html) return html;

  // otimização: se não tem base64, não faz nada
  if (!html.includes('data:image/')) return html;

  const { putImage } = await import('@/utils/idb/imagesDb');

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const imgs = Array.from(doc.querySelectorAll('img'));
  if (!imgs.length) return html;

  // dedupe: mesma dataUri -> mesma key (evita salvar duplicado no IDB)
  const seen = new Map();

  draft.images ||= [];

  for (const img of imgs) {
    const src = img.getAttribute('src') || '';
    const mceSrc = img.getAttribute('data-mce-src') || '';

    // prioriza data-mce-src se for data URI
    const candidate =
      (mceSrc.startsWith('data:image/')) ? mceSrc :
      (src.startsWith('data:image/')) ? src :
      null;

    if (!candidate) continue;

    let key = seen.get(candidate);

    if (!key) {
      const blob = dataUriToBlob(candidate);
      key = makeIdbImageKey(userId);
      await putImage(blob, key);
      seen.set(candidate, key);
    }

    img.setAttribute('src', `idb://${key}`);
    img.setAttribute('data-mce-src', `idb://${key}`);

    if (!draft.images.includes(key)) draft.images.push(key);
  }

  return doc.body.innerHTML;
}

/**
 * Monta FormData para criar/atualizar Documento (dtype 'D'),
 * enviando:
 * - documento (JSON)
 * - imagem (capa) opcional
 * - inlineKeys (JSON array) opcional
 * - inlineImages (blobs) opcional
 *
 * Regras:
 * - garante que o HTML esteja com idb://... (substitui blob: usando draft.imageMap)
 * - extrai keys idb://... do HTML
 * - para cada key, pega o Blob no IndexedDB e anexa em inlineImages
 */
export async function buildDocumentoFormData(documentoDto, capaFile, draft) {
  // 1) garante HTML com idb:// (para o backend conseguir mapear)
  const rawHtml = draft?.data?.texto || '';
  const html = replaceBlobToIdb(rawHtml, draft?.imageMap || {});

  // 2) extrai keys idb://
  const keys = extractIdbKeys(html);

  // 3) documentoDto precisa levar o HTML final (campo do seu DTO é "conteudo")
  documentoDto.conteudo = html;

  // 4) monta multipart/form-data
  const fd = new FormData();
  fd.append('documento', new Blob([JSON.stringify(documentoDto)], { type: 'application/json' }));

  if (capaFile) {
    // Se for File, mantém; se for Blob, fornece filename (como você fazia antes)
    const isFile = (typeof File !== 'undefined') && (capaFile instanceof File);

    if (isFile) {
      fd.append('imagem', capaFile);
    } else {
      const type = capaFile.type || 'image/jpeg';
      const extRaw = type.split('/')?.[1] || 'jpg';
      const ext = (extRaw === 'jpeg') ? 'jpg' : extRaw;
      fd.append('imagem', capaFile, `existing_cover.${ext}`);
    }
  }

  // 5) inline images (se houver)
  if (keys.length) {
    fd.append('inlineKeys', JSON.stringify(keys));

    // import dinâmico (evita dependência circular / bundle desnecessário)
    const { getImage } = await import('@/utils/idb/imagesDb');

    for (const key of keys) {
      const blob = await getImage(key);
      if (!blob) continue;

      // tenta derivar extensão pelo mime; fallback png
      const ext = (blob.type && blob.type.includes('/'))
        ? blob.type.split('/')[1]
        : 'png';

      // evita caracteres ruins no filename (mas mantém key como identificador)
      const safeKey = String(key).replace(/[^a-zA-Z0-9._-]/g, '_');

      fd.append('inlineImages', blob, `inline-${safeKey}.${ext}`);
    }
  }

  return fd;
}

export function resolveUploadUrl(path, dominio) {
  if (!path) return path;

  // já é absoluta
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }

  // garante apenas uma barra
  return `${dominio.replace(/\/$/, '')}${path.startsWith('/') ? '' : '/'}${path}`;
}
